import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  EnvelopeIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UserPlusIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import {
  getPendingInvitations,
  inviteTeamMember,
  updateMemberRole,
  removeMember,
  cancelInvitation
} from '../../utils/companyClient';

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member', description: 'Full access to projects and sessions' },
  { value: 'admin', label: 'Admin', description: 'Can manage team and settings' },
  { value: 'owner', label: 'Owner', description: 'Full access including billing' },
];

const ROLE_COLORS = {
  owner: 'bg-gray-100 text-gray-700 border border-gray-300',
  admin: 'bg-gray-100 text-gray-700 border border-gray-300',
  member: 'bg-gray-100 text-gray-700 border border-gray-300'
};

const ROLE_HIERARCHY = {
  member: 1,
  admin: 2,
  owner: 3
};

export default function TeamManagement({ 
  company, 
  teamMembers, 
  currentUser, 
  userRole, 
  canManage, 
  onRefresh 
}) {
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showJoinHelpModal, setShowJoinHelpModal] = useState(false);
  const [showRolesHelpModal, setShowRolesHelpModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [memberToChangeRole, setMemberToChangeRole] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load pending invitations
  useEffect(() => {
    if (canManage && company?.id) {
      loadPendingInvitations();
    }
  }, [company?.id, canManage]);

  const loadPendingInvitations = async () => {
    try {
      const invitations = await getPendingInvitations(company.id);
      setPendingInvitations(invitations);
    } catch (error) {
      console.error('Error loading pending invitations:', error);
    }
  };

  // Handle role change
  const handleRoleChange = async (newRole) => {
    if (!memberToChangeRole) return;
    setActionLoading(true);
    try {
      await updateMemberRole(memberToChangeRole.id, newRole);
      setShowRoleModal(false);
      setMemberToChangeRole(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle member removal
  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    setActionLoading(true);
    try {
      await removeMember(memberToRemove.id);
      setShowRemoveModal(false);
      setMemberToRemove(null);
      onRefresh();
    } catch (error) {
      console.error('Error removing member:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle invitation cancellation
  const handleCancelInvite = async (invitationId) => {
    try {
      await cancelInvitation(invitationId);
      await loadPendingInvitations();
    } catch (error) {
      console.error('Error canceling invitation:', error);
    }
  };

  // Check if user can modify another user's role
  const canModifyMember = (member) => {
    if (!canManage) return false;
    if (member.user_id === currentUser?.id) return false;
    const currentUserLevel = ROLE_HIERARCHY[userRole] || 0;
    const targetUserLevel = ROLE_HIERARCHY[member.role] || 0;
    return currentUserLevel > targetUserLevel;
  };

  // Get available roles for dropdown based on current user's role
  const getAvailableRoles = () => {
    const currentUserLevel = ROLE_HIERARCHY[userRole] || 0;
    return ROLE_OPTIONS.filter(role => ROLE_HIERARCHY[role.value] < currentUserLevel);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Team Members Table */}
      <div className="bg-white border-b border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''} in {company?.name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowJoinHelpModal(true)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
              >
                <UserPlusIcon className="h-3.5 w-3.5 mr-1.5" />
                How to Join
              </button>
              <button
                onClick={() => setShowRolesHelpModal(true)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors"
              >
                <ShieldCheckIcon className="h-3.5 w-3.5 mr-1.5" />
                Role Guide
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th scope="col" className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th scope="col" className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Joined
              </th>
              <th scope="col" className="w-24 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {teamMembers.map((member) => {
              const canModify = canModifyMember(member);
              const isCurrentUser = member.user_id === currentUser?.id;

              return (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {member.full_name}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs text-red-600">(You)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {member.profile?.email || '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {canModify ? (
                      <button
                        onClick={() => {
                          setMemberToChangeRole(member);
                          setShowRoleModal(true);
                        }}
                        className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700 border border-gray-300'} hover:opacity-80 transition-opacity cursor-pointer`}
                      >
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </button>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium ${ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-700 border border-gray-300'}`}>
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(member.created_at)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right">
                    {canModify && (
                      <button
                        onClick={() => {
                          setMemberToRemove(member);
                          setShowRemoveModal(true);
                        }}
                        className="text-sm text-gray-500 hover:text-red-600 hover:underline transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {teamMembers.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <UsersIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No team members found</p>
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {canManage && pendingInvitations.length > 0 && (
        <div className="bg-white rounded-sm shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Pending Invitations
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {pendingInvitations.length} pending
            </p>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invited
                </th>
                <th scope="col" className="w-24 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pendingInvitations.map((invitation) => (
                <tr key={invitation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{invitation.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium ${ROLE_COLORS[invitation.role]}`}>
                      {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invitation.created_at)}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleCancelInvite(invitation.id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemoveModal && memberToRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Remove Member</h3>
              </div>
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setMemberToRemove(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to remove <strong>{memberToRemove.full_name}</strong> from {company?.name}? 
                They will lose access to all projects and data.
              </p>
            </div>

            <div className="flex space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setMemberToRemove(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 text-sm font-medium"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
              >
                {actionLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && memberToChangeRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Change Role</h3>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setMemberToChangeRole(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Select a new role for <strong>{memberToChangeRole.full_name}</strong>
              </p>
              <div className="space-y-2">
                {getAvailableRoles().map((role) => (
                  <button
                    key={role.value}
                    onClick={() => handleRoleChange(role.value)}
                    disabled={actionLoading || role.value === memberToChangeRole.role}
                    className={`w-full text-left p-3 border rounded-sm transition-colors ${
                      role.value === memberToChangeRole.role
                        ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium ${ROLE_COLORS[role.value]}`}>
                          {role.label}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                      </div>
                      {role.value === memberToChangeRole.role && (
                        <span className="text-xs text-gray-400">Current</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setMemberToChangeRole(null);
                }}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How to Join Modal */}
      {showJoinHelpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <UserPlusIcon className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">How to Add Team Members</h3>
              </div>
              <button
                onClick={() => setShowJoinHelpModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-semibold">1</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Share your invite code</p>
                    <p className="text-xs text-gray-500 mt-0.5">Your team's invite code is shown in the Settings header. Share this code with new team members.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-semibold">2</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">New member creates an account</p>
                    <p className="text-xs text-gray-500 mt-0.5">They sign up at the login page and create a new account with their email.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-semibold">3</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Enter the invite code</p>
                    <p className="text-xs text-gray-500 mt-0.5">During signup, they'll be prompted to enter the invite code to join your team.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-semibold">4</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Set their role</p>
                    <p className="text-xs text-gray-500 mt-0.5">Once they join, you can adjust their role from this Team page.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowJoinHelpModal(false)}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Guide Modal */}
      {showRolesHelpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Team Roles</h3>
              </div>
              <button
                onClick={() => setShowRolesHelpModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="p-3 border border-gray-200 rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">Owner</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">Full Access</span>
                </div>
                <p className="text-xs text-gray-500">Full access including billing, subscription management, and company settings. Can manage all team members.</p>
              </div>

              <div className="p-3 border border-gray-200 rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">Admin</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">Settings Access</span>
                </div>
                <p className="text-xs text-gray-500">Can manage team members and company settings. No access to billing or subscription.</p>
              </div>

              <div className="p-3 border border-gray-200 rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900">Member</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">Standard</span>
                </div>
                <p className="text-xs text-gray-500">Full access to projects and sessions. No access to team management or settings.</p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowRolesHelpModal(false)}
                className="w-full px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}